import { Box, Button } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import { ErrorMessage } from "@outschool/ui-components-shared";
import { Loading } from "@outschool/ui-legacy-component-library";
import { useLoadMoreIfEndOfScreen } from "@outschool/ui-utils";
import React from "react";

import {
  PublicReviewQuote,
  PublicReviewsOfLeaderQueryOptions,
  usePublicReviewsOfLeaderQuery,
} from "../index";

type PublicReviewsOfLeaderInfiniteScrollProps = {
  leaderUid: string;
  ReviewRespondButton?: React.ElementType;
};

const PublicReviewsOfLeaderInfiniteScroll: React.FunctionComponent<
  PublicReviewsOfLeaderInfiniteScrollProps
> = ({ leaderUid, ReviewRespondButton }) => {
  const { t } = useTranslation(
    "ui-components-website\\src\\Reviews\\PublicReviewsOfLeaderInfiniteScroll",
  );
  const options: PublicReviewsOfLeaderQueryOptions = {
    skip: !leaderUid,
    variables: {
      leaderUid,
      offset: 0,
      limit: 10,
      skipAggregates: true,
      includeSummary: false,
    },
  };

  const { data, error, loading, fetchMoreReviews, isFetchingMore } =
    usePublicReviewsOfLeaderQuery(options);

  const [endOfScreenRef, setEndOfScreenRef] = React.useState<HTMLDivElement>();
  useLoadMoreIfEndOfScreen({
    endOfScreenRef,
    fetchMore: fetchMoreReviews,
    isInfiniteScrollEnabled: true,
  });

  /** handle refetch and refetch errors */
  const [refetchError, setRefetchError] = React.useState<string | null>(null);
  const handleFetchMoreReviews = React.useCallback(async () => {
    try {
      await fetchMoreReviews();
    } catch (e) {
      setRefetchError(e);
    }
  }, [fetchMoreReviews]);

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {/* @ts-ignore TS(2531): Object is possibly 'null'. */}
      {data?.publicProfile.leader.recentReviews.map(review => (
        <PublicReviewQuote
          containerSx={{ marginBottom: "1em" }}
          showMoreMaxChars={200}
          // @ts-ignore TS(2531): Object is possibly 'null'.
          teacher={data.publicProfile.leader}
          activity={review.activity}
          review={review}
          key={review.uid + "/quote"}
          trackingLabel="public_review_of_leader"
          ReviewRespondButton={ReviewRespondButton}
        />
      ))}

      <Box
        ref={setEndOfScreenRef}
        sx={{
          height: "10px",
        }}
      />

      {/* if there's an error, give the user the option to manually fetch more reviews */}
      {(error || refetchError) && (
        <>
          <ErrorMessage
            value={t("Oops, we encountered an error when loading reviews.")}
          />
          <Box
            flex
            sx={{
              justifyContent: "center",
            }}
          >
            <Button onClick={handleFetchMoreReviews}>
              {t("Try loading more reviews again")}
            </Button>
          </Box>
        </>
      )}

      {isFetchingMore && (
        <Box
          sx={{
            paddingBottom: "1em",
          }}
        >
          <Loading delay={0} />
        </Box>
      )}
    </>
  );
};
export default PublicReviewsOfLeaderInfiniteScroll;
